<template>
    <v-expansion-panel-header @click.native="$emit('click')" class="block-separator">
        <span class="block-num">{{ index + 1 }}</span>
        <div class="block-row">
            <template v-if="!hideStudentInfo">
                <timer-dialog v-if="block.isCircuit" :type="block.type" :duration="block.sets * 60"
                    :tabata="block.tabata" @hide="setBlockComplete(true)" ref="timer" />
                <kimsa-toggle class="block-toggle" :value="block.completed" :loading="loadingComplete && clicked"
                    :disabled="readonly" @change="setBlockComplete" @click.native.stop />
            </template>
            <div class="block-info">
                <div class="block-name">
                    {{ $tc('common.blocks', 1) }} {{ index + 1 }} {{ circuitType }}
                </div>
                <div class="block-meta">
                    <span>
                        <v-icon size="13">mdi-dumbbell</v-icon>
                        {{ exercisesText }}
                    </span>
                    <span>
                        <v-icon size="13">{{ durationIcon }}</v-icon>
                        {{ blockDuration }}
                    </span>
                </div>
            </div>
        </div>
    </v-expansion-panel-header>
</template>

<script>
import TimerDialog from './TimerDialog.vue'
import KimsaToggle from '@/components/common/inputs/KimsaToggle'
export default {
    components: { TimerDialog, KimsaToggle },
    name: "BlockExpansionHeader",
    props: {
        hideStudentInfo: {
            type: Boolean,
            default: false,
        },
        block: {
            type: Object,
            default: null,
        },
        loadingComplete: {
            type: Boolean,
            default: false,
        },
        readonly: {
            type: Boolean,
            default: false,
        },
        index: {
            type: [Number, String],
            default: 0
        }
    },
    data() {
        return {
            clicked: false,
        }
    },
    computed: {
        durationIcon(vm = this) {
            if (vm.block.type === 'time') return 'mdi-refresh'
            return 'mdi-clock-outline'
        },
        blockDuration(vm = this) {
            if (vm.block.isCircuit) {
                let sets = vm.block.sets
                if (vm.block.type === 'time') {
                    return `${sets} ${vm.$t('common.laps').toUpperCase()}`
                }
                if (vm.block.type === 'tabata') {
                    let minutes = vm.block.tabata.minutes
                    let timeWork = vm.block.tabata.timeWork
                    let timeRest = vm.block.tabata.timeRest
                    return `(${timeWork}"X${timeRest}"X${minutes}')X${sets}'`
                }
                return `${sets}'`
            }
            let minutes = 0
            vm.block.exercises.forEach(exercise => {
                minutes += exercise?.config?.sets?.length || vm.block.sets || 0
            })
            let minutesAdded = Number(minutes * 1.3).toFixed()

            let minutesString = vm.$tc(`common.minutes`, Number(minutes) === 1 ? 1 : 2)
            if (Number(minutes) === Number(minutesAdded)) return `${minutes} ${minutesString.toLowerCase()}`
            return `${minutes}-${minutesAdded} ${minutesString.toLowerCase()}`
        },
        exercisesText(vm = this) {
            let exercises = vm.block.exercises?.filter(ex => ex.type === 'exercise').length || 0
            return `${exercises}  ${vm.$tc('exercises.exercises', exercises > 1 ? 2 : 1)}`
        },
        circuitType(vm = this) {
            if (!vm.block.isCircuit) return null
            return `- ${vm.$t(`circuits.${vm.block.type}`)}`
        }
    },
    methods: {
        setBlockComplete(completed) {
            const vm = this
            vm.clicked = true
            vm.$emit('complete', completed, vm.index)
        },
        showTimerDialog() {
            const vm = this;
            vm.$refs.timer.show()
        }
    },
    watch: {
        loadingComplete(val) {
            if (!val) this.clicked = false
        }
    }
}
</script>

<style scoped>
.block-separator {
    position: relative;
    overflow: hidden;
    background: linear-gradient(135deg, #f0f4f8 0%, #e8edf4 100%);
    border: 1px solid rgba(51, 153, 153, 0.15);
    padding: 12px 14px;
    --block-accent: var(--v-accent-base);
}

.theme--dark .block-separator {
    background: linear-gradient(135deg, #1a1f2e 0%, #141824 100%);
    border-color: rgba(204, 255, 51, 0.12);
    --block-accent: var(--v-primary-base);
}

.block-num {
    position: absolute;
    right: 44px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 52px;
    font-weight: 900;
    line-height: 1;
    letter-spacing: -2px;
    color: rgba(51, 153, 153, 0.09);
    pointer-events: none;
    z-index: 0;
}

.theme--dark .block-num {
    color: rgba(204, 255, 51, 0.08);
}

.block-row {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    min-width: 0;
    position: relative;
    z-index: 1;
}

.block-toggle {
    flex-shrink: 0;
}

.block-info {
    flex: 1;
    min-width: 0;
}

.block-name {
    font-size: 13px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: var(--block-accent);
}

.block-meta {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 4px;
    font-size: 11px;
    color: var(--v-txtMuted-base);
}

.block-meta span {
    display: flex;
    align-items: center;
    gap: 4px;
}

.block-meta .v-icon {
    color: var(--v-txtMuted-base) !important;
}

>>>.v-expansion-panel-header__icon>i {
    color: var(--block-accent) !important;
}
</style>